import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { LIGHT_TYPE } from '@src/components/scene/react-three-components/lightTemplate';
import { getBranding } from 'util/constants';
import { Brand } from '@src/components/generated/graphql';
import { DEFAULT_LIGHT_TEMPLATE } from '../../Editor';
import { calculateSceneBounds } from './calculate-scene-bounds';

export default async function saveScene({
  saveNewTemplate,
  isSignIn,
  router,
  createScene,
  user,
  setSelectedModel,
  setIsTransformControlsActive,
  setIsEditorDrawerOpen,
  updateScene,
  selectedSkybox,
  lightTemplate,
  sceneAssets,
  selectableAssets,
  assetChanged,
  updateAsset,
  createAsset,
  cleanUpDeletedAssets,
  setSceneAssets,
  setSelectableAssets,
  openNotification,
  defaultSceneTemplate,
  selectedSceneThumbnail,
  updateManyAssets,
}) {
  if (!isSignIn) {
    saveNewTemplate();
    return;
  }

  let sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  let createdTemplate = false;

  if (router.pathname === '/new-template') {
    const res = await createScene({
      variables: {
        data: {
          name: 'New template',
          description: `New template at ${new Date().toJSON()}`,
          thumbnail: defaultSceneTemplate?.thumbnail ?? '',
          userId: user.id,
          branding: getBranding() === 'ZEPETO' ? Brand.Zepeto : Brand.Studio,
          lightTemplate: DEFAULT_LIGHT_TEMPLATE,
        },
      },
    });

    sceneId = res.data?.createOneUserScene.id;
    createdTemplate = true;
  }

  setSelectedModel(null);
  setIsTransformControlsActive(false);
  setIsEditorDrawerOpen(() => false);

  const sceneBounds = calculateSceneBounds(sceneAssets, selectableAssets);

  await updateScene({
    variables: {
      data: {
        skybox: {
          set: selectedSkybox.index,
        },
        isSkyBox2025: {
          set: selectedSkybox.isSkyBox2025,
        },
        hdri: {
          set: selectedSkybox.hdriType,
        },
        envIntensity: {
          set: selectedSkybox.hdriIntensity,
        },
        envMapType: {
          set: selectedSkybox.envMapType,
        },
        lightTemplate: {
          set: lightTemplate,
        },
        thumbnail: {
          set: selectedSceneThumbnail,
        },
        sceneBounds: {
          set: sceneBounds,
        },
      },
      where: {
        id: sceneId,
      },
    },
  });

  const allAssets = [...sceneAssets, ...selectableAssets];
  const sceneAssetLength = sceneAssets.length;
  const promises: any = [];
  const assetsToUpdate: any = [];

  for (let i = 0; i < allAssets.length; i++) {
    const asset = allAssets[i];
    const sceneAsset = asset.props.asset;

    if (sceneAsset.type === ASSET_TYPE.PRIMITIVE.POSITION_POINT) {
      continue;
    }
    const threeObj = asset.props.asset.ref.current;

    if (!threeObj) {
      continue;
    }

    let baseColor, roughness, metalness;
    threeObj.traverse((child: any) => {
      if (child.isMesh) {
        baseColor = child.material.color;
        roughness = child.material.roughness;
        metalness = child.material.metalness;
      }
    });

    const assetExists = sceneAsset.id;

    let light: any = undefined;

    if (sceneAsset.type === ASSET_TYPE.MISC.LIGHT) {
      const lightThreeObj = asset.props.light.ref.current;

      const lightType = asset.props.light.type;

      if (assetExists) {
        light = {
          update: {
            data: {
              color: {
                set: lightThreeObj.color,
              },
              intensity: {
                set: lightThreeObj.intensity,
              },

              ...(lightThreeObj.shadow
                ? {
                    shadowMapWidth: { set: lightThreeObj.shadow.mapSize.width },
                    shadowMapHeight: {
                      set: lightThreeObj.shadow.mapSize.height,
                    },
                    shadowBias: { set: lightThreeObj.shadow.bias },
                    shadowCameraNear: { set: lightThreeObj.shadow.camera.near },
                    shadowCameraFar: { set: lightThreeObj.shadow.camera.far },
                    shadowCameraLeft: {
                      set: lightThreeObj.shadow.camera.left || 0,
                    },
                    shadowCameraRight: {
                      set: lightThreeObj.shadow.camera.right || 0,
                    },
                    shadowCameraTop: {
                      set: lightThreeObj.shadow.camera.top || 0,
                    },
                    shadowCameraBottom: {
                      set: lightThreeObj.shadow.camera.bottom || 0,
                    },
                    shadow: { set: true },
                  }
                : {}),

              ...((lightType === LIGHT_TYPE.SPOTLIGHT ||
                lightType === LIGHT_TYPE.POINTLIGHT) && {
                distance: { set: lightThreeObj.distance },
                decay: { set: lightThreeObj.decay },
              }),
              ...(lightType === LIGHT_TYPE.SPOTLIGHT && {
                angle: { set: lightThreeObj.angle },
                penumbra: { set: lightThreeObj.penumbra },
              }),
              ...(lightType === LIGHT_TYPE.HEMISPHERELIGHT && {
                groundColor: { set: lightThreeObj.groundColor },
                skyColor: { set: lightThreeObj.color },
              }),
            },
          },
        };
      } else {
        light = {
          create: {
            color: lightThreeObj.color,
            intensity: lightThreeObj.intensity,
            type: lightType,
            ...(lightThreeObj.shadow
              ? {
                  shadowMapWidth: lightThreeObj.shadow.mapSize.width,
                  shadowMapHeight: lightThreeObj.shadow.mapSize.height,
                  shadowBias: lightThreeObj.shadow.bias,
                  shadowCameraNear: lightThreeObj.shadow.camera.near,
                  shadowCameraFar: lightThreeObj.shadow.camera.far,
                  shadowCameraLeft: lightThreeObj.shadow.camera.left,
                  shadowCameraRight: lightThreeObj.shadow.camera.right,
                  shadowCameraTop: lightThreeObj.shadow.camera.top,
                  shadowCameraBottom: lightThreeObj.shadow.camera.bottom,
                  shadow: true,
                }
              : {}),
            ...((lightType === LIGHT_TYPE.SPOTLIGHT ||
              lightType === LIGHT_TYPE.POINTLIGHT) && {
              distance: lightThreeObj.distance,
              decay: lightThreeObj.decay,
            }),
            ...(lightType === LIGHT_TYPE.SPOTLIGHT && {
              angle: lightThreeObj.angle,
              penumbra: lightThreeObj.penumbra,
            }),
            ...(lightType === LIGHT_TYPE.HEMISPHERELIGHT && {
              groundColor: lightThreeObj.groundColor,
              skyColor: lightThreeObj.color,
            }),
          },
        };
      }
    }

    try {
      if (assetExists) {
        if (!assetChanged(sceneAsset, threeObj)) {
          continue;
        }
        const updateAsset = {
          id: {
            set: sceneAsset.id,
          },
          url: {
            set: sceneAsset.url,
          },
          textureToChange: {
            set: sceneAsset?.textureToChange || '',
          },
          previewImage: {
            set: sceneAsset?.previewImage || '',
          },
          textureToChangeNormal: {
            set: sceneAsset?.textureToChangeNormal || '',
          },
          textureToChangeRoughness: {
            set: sceneAsset?.textureToChangeRoughness || '',
          },
          textureToChangeMetalness: {
            set: sceneAsset?.textureToChangeMetalness || '',
          },
          baseColor: {
            set: baseColor || 1.0,
          },
          roughness: {
            set: roughness || 1.0,
          },
          metalness: {
            set: metalness || 1.0,
          },

          name: {
            set: sceneAsset.name,
          },
          position: {
            set: [
              threeObj.position.x,
              threeObj.position.y,
              threeObj.position.z,
            ],
          },
          rotation: {
            set: [
              threeObj.rotation.x,
              threeObj.rotation.y,
              threeObj.rotation.z,
            ],
          },
          scale: {
            set: [threeObj.scale.x, threeObj.scale.y, threeObj.scale.z],
          },
          castShadow: {
            set: threeObj.castShadow,
          },
          receiveShadow: {
            set: threeObj.receiveShadow,
          },
          metadata: {
            set: JSON.stringify(sceneAsset.metadata || {}),
          },
          showOnMinimap: {
            set: sceneAsset.showOnMinimap || false,
          },
          autoPlayVideo: threeObj.autoPlay,
          loopVideo: threeObj.loop,
          light,
        };

        assetsToUpdate.push(updateAsset);
      } else {
        let metadata;

        try {
          if (sceneAsset?.shopifyMetadata) {
            metadata = JSON.stringify(sceneAsset.shopifyMetadata);
          }
        } catch (error) {
          console.error('Error during parsing the shopify metadata');
        }

        const create = new Promise(async (res, rej) => {
          const assetResult = await createAsset({
            variables: {
              data: {
                name: sceneAsset.name,
                isPortal: sceneAsset?.isPortal as any,
                description: `Scene ${sceneId} asset ${sceneAsset.name}`,
                type: sceneAsset.type.toString(),
                marketId: sceneAsset?.marketId ?? undefined,
                shopifyMetadata: metadata ?? undefined,
                url: sceneAsset.url,
                previewImage: sceneAsset?.previewImage || sceneAsset?.url || '',
                textureToChange: sceneAsset?.textureToChange || '',
                showOnMinimap: sceneAsset.showOnMinimap || false,
                textureToChangeRoughness:
                  sceneAsset?.textureToChangeRoughness || '',
                textureToChangeMetalness:
                  sceneAsset?.textureToChangeMetalness || '',
                textureToChangeNormal: sceneAsset?.textureToChangeNormal || '',
                baseColor: baseColor || 0xffffff,
                roughness: roughness || -1.0,
                metalness: metalness || -1.0,
                userId: user.id,
                metadata: JSON.stringify(sceneAsset.metadata || {}),
                position: {
                  set: [
                    threeObj.position.x,
                    threeObj.position.y,
                    threeObj.position.z,
                  ],
                },
                rotation: {
                  set: [
                    threeObj.rotation.x,
                    threeObj.rotation.y,
                    threeObj.rotation.z,
                  ],
                },
                scale: {
                  set: [threeObj.scale.x, threeObj.scale.y, threeObj.scale.z],
                },
                castShadow: threeObj.castShadow,
                receiveShadow: threeObj.receiveShadow,
                ...(threeObj.autoPlay !== undefined && {
                  autoPlayVideo: threeObj.autoPlay,
                }),
                ...(threeObj.loop !== undefined && {
                  loopVideo: threeObj.loop,
                }),
                userScene: {
                  connect: {
                    id: sceneId,
                  },
                },
                light,
              },
            },
            refetchQueries: ['FindManyUserSceneAssets'],
          });

          sceneAsset.id = assetResult.data?.createOneUserSceneAssets.id;

          if (sceneAsset.type === ASSET_TYPE.MISC.LIGHT) {
            asset.props.light.id =
              assetResult.data?.createOneUserSceneAssets.lightId;
          }
          res(true);
        });
        promises.push(create);
      }
    } catch (error) {
      console.log('error', sceneAsset.name, error);
    }
  }

  await Promise.allSettled(promises);
  await cleanUpDeletedAssets();

  const update = await updateManyAssets({
    variables: {
      data: {
        assets: assetsToUpdate,
      },
    },
  });

  if (!update.data?.updateSceneAssets.status) {
    openNotification('Studio', 'Error when save the scene');
    return;
  }

  const updatedSceneAssets = allAssets.splice(0, sceneAssetLength);

  const updatedSelectableAssets = allAssets;

  setSceneAssets(() => updatedSceneAssets);
  setSelectableAssets({ type: 'set', newState: updatedSelectableAssets });

  openNotification('Studio', 'Scene saved successfully');

  if (createdTemplate) {
    window.localStorage.removeItem('new-template');
    router.push(`/user/studio/scene/${sceneId}`);
  }
  return;
}
